import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, ShieldCheck, Building2, Landmark, Loader2, AlertCircle } from 'lucide-react';
import { GDLLogo } from './GDLLogo';
import './gdl.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * GDL Two-path Onboarding (M1).
 *
 * Screens:
 *   0 — Path selection (Civil servant vs Corporate)
 *   1 — Phone + OTP (mock 111111)
 *   2 — Civil servant details (Federal/State) OR Corporate details
 *   3 — Verifying animation → outcome (verified or under review)
 *   4 — Set PIN
 *   5 — Landing (redirect to dashboard)
 */
export default function GDLOnboard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [path, setPath] = useState(null); // 'federal' | 'state' | 'corporate'
  const [ministries, setMinistries] = useState([]);
  const [states, setStates] = useState([]);
  const [grades, setGrades] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [form, setForm] = useState({
    full_name: '', phone: '', bvn: '', otp: '',
    ministry: '', state: '', grade_level: 8, step: 1,
    monthly_salary: 450000, employee_number: '',
    employer_id: '', staff_id: '', work_email: '', pin: '',
  });
  const [verifyState, setVerifyState] = useState({ inflight: false, outcome: null, checks: null });
  const [error, setError] = useState('');
  const [authToken, setAuthToken] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/api/demo/gdl/meta/ministries`).then(r => setMinistries(r.data.ministries));
    axios.get(`${API}/api/demo/gdl/meta/states`).then(r => setStates(r.data.states));
    axios.get(`${API}/api/demo/gdl/meta/grade-levels`).then(r => setGrades(r.data.grades));
    axios.get(`${API}/api/demo/gdl/meta/employers`).then(r => setEmployers(r.data.employers)).catch(() => setEmployers([]));
  }, []);

  const startPath = (p) => {
    setPath(p);
    setError('');
    setStep(1);
  };

  const submitOtp = () => {
    if (form.otp !== '111111') { setError('Enter 111111 to continue'); return; }
    setError(''); setStep(2);
  };

  const submitDetails = async () => {
    setError(''); setVerifyState({ inflight: true, outcome: null, checks: null });
    setStep(3);
    try {
      let payload, endpoint;
      if (path === 'corporate') {
        endpoint = '/api/demo/gdl/onboard/corporate';
        payload = {
          full_name: form.full_name, phone: form.phone, bvn: form.bvn,
          employer_id: form.employer_id, staff_id: form.staff_id, work_email: form.work_email,
          pin: '0000',  // temp — replaced at step 4
        };
      } else {
        endpoint = '/api/demo/gdl/onboard/civil';
        payload = {
          path, full_name: form.full_name, phone: form.phone, bvn: form.bvn,
          ministry: form.ministry, grade_level: parseInt(form.grade_level),
          step: parseInt(form.step), monthly_salary: parseFloat(form.monthly_salary),
          employee_number: form.employee_number,
          state: path === 'state' ? form.state : undefined,
          pin: '0000',
        };
      }
      // Small artificial delay for realism (2-4s target from spec)
      await new Promise(r => setTimeout(r, 2500));
      const { data } = await axios.post(`${API}${endpoint}`, payload);
      setAuthToken(data.token);
      setVerifyState({ inflight: false, outcome: data.outcome, checks: null });
      // Also fetch the verification-request detail for check breakdown
      if (data.verification_request_id) {
        try {
          const detail = await axios.get(`${API}/api/demo/gdl/verify/requests/${data.verification_request_id}`);
          setVerifyState(s => ({ ...s, checks: detail.data.checks }));
        } catch (_) {}
      }
    } catch (err) {
      setVerifyState({ inflight: false, outcome: 'error', checks: null });
      setError(err.response?.data?.detail || 'Verification failed');
    }
  };

  const submitPin = async () => {
    // Persist pin by re-submitting? For M1 the pin is set during onboard.
    // We simply store token + move on.
    localStorage.setItem('gdl_token', authToken);
    nav('/demo/gdl/dashboard');
  };

  // ---------------------------------------------------------
  // Render
  // ---------------------------------------------------------
  return (
    <div className="gdl-onboard-shell">
      <div className="gdl-onboard-card" data-testid="gdl-onboard-card">
        <div className="gdl-onboard-brand"><GDLLogo /></div>

        {step === 0 && (
          <div className="gdl-onboard-body">
            <h1 className="gdl-h1">Welcome to GDL Finance</h1>
            <p className="gdl-muted">Select how you receive your salary to get started.</p>
            <div className="gdl-path-grid">
              <button className="gdl-path-tile" data-testid="path-civil-servant" onClick={() => setStep(0.5)}>
                <Landmark size={32} />
                <span className="gdl-path-title">I am a civil servant</span>
                <span className="gdl-muted small">Federal (IPPIS) or state payroll</span>
              </button>
              <button className="gdl-path-tile" data-testid="path-corporate" onClick={() => startPath('corporate')}>
                <Building2 size={32} />
                <span className="gdl-path-title">I work for a company or parastatal</span>
                <span className="gdl-muted small">Verified via employer roster</span>
              </button>
            </div>
          </div>
        )}

        {step === 0.5 && (
          <div className="gdl-onboard-body">
            <h1 className="gdl-h1">Which payroll?</h1>
            <p className="gdl-muted">We verify your record against the appropriate payroll registry.</p>
            <div className="gdl-path-grid">
              <button className="gdl-path-tile" data-testid="path-federal" onClick={() => startPath('federal')}>
                <ShieldCheck size={28} /><span className="gdl-path-title">Federal (IPPIS)</span>
              </button>
              <button className="gdl-path-tile" data-testid="path-state" onClick={() => startPath('state')}>
                <ShieldCheck size={28} /><span className="gdl-path-title">State civil service</span>
              </button>
            </div>
            <button className="gdl-btn-ghost" onClick={() => setStep(0)}>Back</button>
          </div>
        )}

        {step === 1 && (
          <div className="gdl-onboard-body">
            <h1 className="gdl-h1">Verify your phone number</h1>
            <label className="gdl-label">Phone</label>
            <input className="gdl-input" data-testid="phone-input"
                   placeholder="+2348012345678" value={form.phone}
                   onChange={e => set('phone', e.target.value)} />
            <label className="gdl-label">Verification code</label>
            <input className="gdl-input" data-testid="otp-input"
                   placeholder="Enter 6-digit code" maxLength={6} value={form.otp}
                   onChange={e => set('otp', e.target.value)} />
            <p className="gdl-muted small">Enter 111111 to continue.</p>
            {error && <p className="gdl-error">{error}</p>}
            <button className="gdl-btn-primary" data-testid="otp-continue-btn" onClick={submitOtp}>Continue</button>
          </div>
        )}

        {step === 2 && path !== 'corporate' && (
          <div className="gdl-onboard-body">
            <h1 className="gdl-h1">Your employment record</h1>
            <p className="gdl-muted">These details are verified against the {path === 'federal' ? 'federal IPPIS' : 'state'} registry.</p>
            <label className="gdl-label">Full name (as on payroll)</label>
            <input className="gdl-input" data-testid="fullname-input"
                   value={form.full_name} onChange={e => set('full_name', e.target.value)} />
            <label className="gdl-label">Bank Verification Number (BVN)</label>
            <input className="gdl-input" data-testid="bvn-input" maxLength={11}
                   value={form.bvn} onChange={e => set('bvn', e.target.value.replace(/\D/g, '').slice(0, 11))} />
            {path === 'state' && (
              <>
                <label className="gdl-label">State</label>
                <select className="gdl-input" data-testid="state-select"
                        value={form.state} onChange={e => set('state', e.target.value)}>
                  <option value="">Select…</option>
                  {states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </>
            )}
            <label className="gdl-label">{path === 'federal' ? 'IPPIS number' : 'State employee number'}</label>
            <input className="gdl-input" data-testid="empno-input"
                   value={form.employee_number} onChange={e => set('employee_number', e.target.value)} />
            <label className="gdl-label">Ministry / Agency</label>
            <select className="gdl-input" data-testid="ministry-select"
                    value={form.ministry} onChange={e => set('ministry', e.target.value)}>
              <option value="">Select…</option>
              {ministries.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <div className="gdl-row-2">
              <div>
                <label className="gdl-label">Grade level</label>
                <select className="gdl-input" data-testid="gl-select"
                        value={form.grade_level} onChange={e => set('grade_level', e.target.value)}>
                  {grades.map(g => <option key={g.level} value={g.level}>GL{String(g.level).padStart(2, '0')}</option>)}
                </select>
              </div>
              <div>
                <label className="gdl-label">Step</label>
                <select className="gdl-input" data-testid="step-select"
                        value={form.step} onChange={e => set('step', e.target.value)}>
                  {Array.from({ length: 15 }, (_, i) => i + 1).map(s => <option key={s} value={s}>Step {s}</option>)}
                </select>
              </div>
            </div>
            <label className="gdl-label">Monthly gross salary (₦)</label>
            <input className="gdl-input" data-testid="salary-input" type="number"
                   value={form.monthly_salary} onChange={e => set('monthly_salary', e.target.value)} />
            {error && <p className="gdl-error">{error}</p>}
            <button className="gdl-btn-primary" data-testid="details-continue-btn" onClick={submitDetails}>Verify my record</button>
          </div>
        )}

        {step === 2 && path === 'corporate' && (
          <div className="gdl-onboard-body">
            <h1 className="gdl-h1">Your employer</h1>
            <p className="gdl-muted">We confirm your record against your employer's HR roster.</p>
            <label className="gdl-label">Full name</label>
            <input className="gdl-input" data-testid="fullname-input"
                   value={form.full_name} onChange={e => set('full_name', e.target.value)} />
            <label className="gdl-label">Bank Verification Number (BVN)</label>
            <input className="gdl-input" data-testid="bvn-input" maxLength={11}
                   value={form.bvn} onChange={e => set('bvn', e.target.value.replace(/\D/g, '').slice(0, 11))} />
            <label className="gdl-label">Employer</label>
            <select className="gdl-input" data-testid="employer-select"
                    value={form.employer_id} onChange={e => set('employer_id', e.target.value)}>
              <option value="">Select…</option>
              {employers.map(e => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
            </select>
            <label className="gdl-label">Staff ID</label>
            <input className="gdl-input" data-testid="staffid-input"
                   value={form.staff_id} onChange={e => set('staff_id', e.target.value)} />
            <label className="gdl-label">Work email</label>
            <input className="gdl-input" data-testid="workemail-input" type="email"
                   value={form.work_email} onChange={e => set('work_email', e.target.value)} />
            {error && <p className="gdl-error">{error}</p>}
            <button className="gdl-btn-primary" data-testid="details-continue-btn" onClick={submitDetails}>Confirm</button>
          </div>
        )}

        {step === 3 && (
          <div className="gdl-onboard-body">
            {verifyState.inflight && (
              <div className="gdl-verify-progress" data-testid="verify-progress">
                <div className="gdl-pulse-ring"><Loader2 size={40} className="gdl-spin" /></div>
                <h2 className="gdl-h2">Verifying your employment record</h2>
                <p className="gdl-muted">Matching against the {path === 'corporate' ? 'employer roster' : path === 'federal' ? 'federal IPPIS' : 'state payroll'} registry…</p>
              </div>
            )}
            {!verifyState.inflight && verifyState.outcome === 'auto_verified' && (
              <div className="gdl-verify-success" data-testid="verify-success">
                <div className="gdl-gold-check"><CheckCircle2 size={48} /></div>
                <h2 className="gdl-h2">You are verified</h2>
                <p className="gdl-muted">Your record was matched successfully.</p>
                <div className="gdl-checks-list">
                  {verifyState.checks && Object.entries(verifyState.checks).map(([k, v]) => (
                    <div key={k} className={`gdl-check-row ${v.pass ? 'ok' : 'ng'}`}>
                      <CheckCircle2 size={14} /><span>{k.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
                <button className="gdl-btn-primary" data-testid="pin-continue-btn" onClick={() => setStep(4)}>Continue to set PIN</button>
              </div>
            )}
            {!verifyState.inflight && (verifyState.outcome === 'manual_review' || verifyState.outcome === 'pending_employer_confirmation') && (
              <div className="gdl-verify-pending" data-testid="verify-pending">
                <div className="gdl-amber-icon"><AlertCircle size={48} /></div>
                <h2 className="gdl-h2">Under review</h2>
                <p className="gdl-muted">
                  {verifyState.outcome === 'pending_employer_confirmation'
                    ? 'Your employer will confirm your record shortly.'
                    : 'Our team will confirm your record within one business day. You can still access limited features while we review.'}
                </p>
                <button className="gdl-btn-primary" data-testid="pin-continue-btn" onClick={() => setStep(4)}>Continue to set PIN</button>
              </div>
            )}
            {!verifyState.inflight && verifyState.outcome === 'error' && (
              <div className="gdl-verify-error">
                <AlertCircle size={40} />
                <h2 className="gdl-h2">Something went wrong</h2>
                <p className="gdl-error">{error}</p>
                <button className="gdl-btn-ghost" onClick={() => setStep(2)}>Try again</button>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="gdl-onboard-body">
            <h1 className="gdl-h1">Set your 4-digit PIN</h1>
            <p className="gdl-muted">You'll use this PIN to authorise money movements.</p>
            <input className="gdl-input gdl-pin-input" data-testid="pin-input" type="password"
                   inputMode="numeric" maxLength={4} value={form.pin}
                   onChange={e => set('pin', e.target.value.replace(/\D/g, '').slice(0, 4))} />
            {form.pin.length === 4 && (
              <button className="gdl-btn-primary" data-testid="finish-onboarding-btn" onClick={submitPin}>Continue</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
